// src/pages/UserProfilePage.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { useAuthContext } from "../context";
import ChatContent from "./ChatContent";
import { Helmet } from "react-helmet";
import { conversationsURL } from "../api/api";
import { Country, State, City } from 'country-state-city';

export default function UserProfilePage() {
  const { userId } = useParams();
  const { allUsers, fetchAllUsers, user: loggedInUser, token } = useAuthContext();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [openChatWith, setOpenChatWith] = useState(null);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [myConversations, setMyConversations] = useState([]);

  const getLocationName = (countryCode, stateCode, cityCode) => {
    const country = Country.getCountryByCode(countryCode);
    const state = State.getStateByCodeAndCountry(stateCode, countryCode);
    const city = cityCode ? 
      City.getCitiesOfState(countryCode, stateCode).find(c => 
        c.isoCode === cityCode || 
        c.name.toLowerCase() === cityCode.toLowerCase()
      ) : null;

    const locationParts = [city?.name, state?.name, country?.name].filter(Boolean);
    if (locationParts.length === 0) {
      return [cityCode, stateCode, countryCode].filter(Boolean).join(', ');
    }
    return locationParts.join(', ');
  };

  const galleryImages = useMemo(() => {
    if (!user?.image) return [];
    let imgs = user.image;
    if (typeof imgs === "string") {
      try { imgs = JSON.parse(imgs); } catch {}
    }
    if (typeof imgs === "string") {
      try { imgs = JSON.parse(imgs); } catch {}
    }
    return Array.isArray(imgs) ? imgs : [];
  }, [user?.image]);

  useEffect(() => {
    document.body.style.overflow = openChatWith || lightboxUrl ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [openChatWith, lightboxUrl]);

  useEffect(() => {
    if (!allUsers.length) fetchAllUsers().catch(console.error);
  }, [allUsers, fetchAllUsers]);

  useEffect(() => {
    const found = allUsers.find((u) => u.user_id === userId);
    if (found) setUser(found);
  }, [allUsers, userId]);

  useEffect(() => {
    if (!token || !loggedInUser?.user_id) return;
    axios
      .get(`${conversationsURL}/${loggedInUser.user_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setMyConversations(res.data))
      .catch(console.error);
  }, [token, loggedInUser]);

  if (!user) {
    return (
      <div className="p-4">
        <div className="flex flex-col justify-center items-center h-64 space-y-4">
          <p className="text-gray-600">Loading profile…</p>
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-red-500"></div>
        </div>
      </div>
    );
  }

  const profileUrl = window.location.href;
  const imageUrl = user.profile_image || "/image/operator.png";
  const isGoldMember =
    loggedInUser?.membership_type === "gold" &&
    loggedInUser?.membership_status === true;

  const existingThread = myConversations.find((conv) =>
    conv.participants.includes(user.user_id)
  );
  const canStartNew =
    isGoldMember || !!existingThread || myConversations.length < 3;

  const needsUpgradeMessages = !canStartNew;
  const needsUpgradeImages = !isGoldMember && galleryImages.length > 1;
  const showUpgradeSection =
    !isGoldMember && (needsUpgradeMessages || needsUpgradeImages);

  const scrollToTop = () =>
    window.scrollTo({ top: 0, behavior: "smooth" });

  const handleMessageClick = () => {
    if (!canStartNew) {
      toast.error("You've reached the free conversation limit. Unlock unlimited messaging with Gold!");
      return;
    }
    setOpenChatWith({
      senderId: loggedInUser.user_id,
      recipientId: user.user_id,
    });
  };
  const handleCloseChat = () => setOpenChatWith(null);

  return (
    <>
      <Helmet>
        <title>{user.firstName} {user.lastName} – SomeOne4u</title>
        <meta name="description" content={`View ${user.firstName}’s profile on SomeOne4u: age ${user.age}, located in ${user.city || user.state || "Unknown"}.`} />
        <meta name="keywords" content={`SomeOne4u, dating, profile, ${user.firstName}, ${user.city}, ${user.state}`} />
        <link rel="canonical" href={profileUrl} />
        <meta property="og:title" content={`${user.firstName} ${user.lastName} – SomeOne4u`} />
        <meta property="og:description" content={`View ${user.firstName}’s profile on SomeOne4u: age ${user.age}, located in ${user.city || user.state || "Unknown"}.`} />
        <meta property="og:url" content={profileUrl} />
        <meta property="og:type" content="profile" />
        <meta property="og:site_name" content="SomeOne4u" />
        <meta property="og:image" content={imageUrl} />
      </Helmet>

      <div
  className={`min-h-screen bg-gradient-to-br from-pink-100 to-purple-100 pb-10 px-4 sm:px-6 ${
    openChatWith || lightboxUrl ? "pointer-events-none select-none" : ""
  }`}
>

<div className="pt-1">
  <div className="mt-12 max-w-4xl mx-auto bg-white shadow rounded-lg overflow-hidden">
    {/* Back Button */}
    <div className="p-4">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-md transition"
      >
        ← Back
      </button>
    </div>

    {/* Profile Info */}
    <div className="flex flex-col items-center text-center">
      <button
        onClick={() => setLightboxUrl(user.profile_image || "/image/operator.png")}
        className="focus:outline-none"
      >
        <img
          src={imageUrl}
          alt="Profile"
          className="w-40 h-40 sm:w-48 sm:h-48 object-cover rounded-full border-4 border-white shadow-xl mb-4 transition-transform hover:scale-105 cursor-pointer"
        />
      </button>
      <h2 className="text-3xl font-bold text-gray-900">
        {user.firstName} {user.lastName}
      </h2>
      <p className="text-gray-600 text-sm mt-1">
        {user.age} yrs • {user.city || "—"}, {user.state || "—"}
      </p>
    </div>

    {/* Message Button */}
    {loggedInUser && loggedInUser.user_id !== user.user_id && (
      <div className="text-center mt-6">
        <button
          onClick={handleMessageClick}
          className={`px-6 py-2 rounded-full text-white text-base font-medium shadow-md transition ${
            canStartNew
              ? "bg-blue-600 hover:bg-blue-700"
              : "bg-gray-400 cursor-not-allowed"
          }`}
        >
          Message
        </button>
      </div>
    )}

    {/* About Me */}
    {user.description && (
      <div className="p-2">
      <div className="mt-10 bg-red-50 border-l-4 border-red-400 p-5 rounded-2xl shadow-sm">
        <h3 className="text-xl font-semibold text-red-500 mb-2">💬 About Me</h3>
        <p className="text-gray-700 text-base leading-relaxed">{user.description}</p>
      </div>
      </div>
    )}

    {/* Personal Details */}
    <div className="mt-8 p-3">
  <h3 className="text-lg font-semibold text-gray-800 mb-2">Personal Details</h3>
  
  <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-2 text-sm text-gray-700">
    {[
      { label: "Gender", value: user.gender },
      { label: "Interested In", value: user.interestedIn },
      {
        label: "Location",
        value: getLocationName(user.country, user.state, user.city),
      },
    ]
      .filter((i) => i.value && i.value !== ", ")
      .map((item, idx) => (
        <div key={idx} className="flex justify-between">
          <span className="font-medium text-gray-800">{item.label}</span>
          <span className="text-right">{item.value}</span>
        </div>
      ))}
  </div>
</div>

    {/* Gallery */}
    {galleryImages.length > 0 && (
      <div className="mt-10">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">📸 Gallery</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {galleryImages.map((src, index) => (
            <img
              key={index}
              src={src}
              alt={`Gallery ${index}`}
              onClick={() => setLightboxUrl(src)}
              className="w-full h-40 object-contain rounded-xl cursor-pointer hover:scale-105 transition-transform shadow"
            />
          ))}
        </div>
      </div>
    )}
  </div>
</div>
</div>

          {showUpgradeSection && (
            <div className="mt-8 bg-yellow-50 border border-yellow-300 rounded-xl p-6 text-center shadow-sm">
              <p className="text-sm text-gray-700 mb-4">
                <strong>Gold Members</strong> can
                {needsUpgradeMessages && " start unlimited conversations"}
                {needsUpgradeMessages && needsUpgradeImages && " and"}
                {needsUpgradeImages && " view all gallery images"}.
              </p>
              <button onClick={() => {navigate("/membership"); scrollToTop();}} className="bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-2 rounded-full shadow transition text-lg">
                Upgrade to Gold
              </button>
            </div>
          )}

      {openChatWith && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50" onClick={handleCloseChat}>
          <div className="bg-white w-full max-w-3xl mx-4 md:mx-auto h-[80vh] rounded-xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <ChatContent senderId={openChatWith.senderId} recipientId={openChatWith.recipientId} onClose={handleCloseChat} />
          </div>
        </div>
      )}

      {lightboxUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 px-4" onClick={() => setLightboxUrl(null)}>
          <div className="relative max-w-3xl w-full">
            <button onClick={() => setLightboxUrl(null)} className="absolute top-2 right-2 text-white text-3xl p-1 bg-black bg-opacity-50 rounded-full hover:bg-opacity-75 transition">&times;</button>
            <img src={lightboxUrl} alt="Preview" className="w-full h-auto max-h-[80vh] object-contain rounded-xl shadow-2xl" />
          </div>
        </div>
      )}
    </>
  );
}
